import { router } from "@inertiajs/react"
import type { ReactNode } from "react"
import { useEffect, useRef, useState } from "react"

import { ConsoleRail } from "@/components/console/rail"
import { MOTION, reducedMotion } from "@/components/ground/engine"
import {
  groundOf,
  useGroundSpec,
  useGroundStore,
} from "@/components/ground/store"
import { useGroundShell } from "@/components/ground/use-ground"
import { cx } from "@/components/ui/cx"
import { LG, useMedia } from "@/hooks/use-media"

/*
 * The staff console: the page is stone, the rail is print, and the work
 * happens on a sheet of paper that overlaps the rail and lifts off the desk.
 * Below lg the rail folds into a bar and the sheet becomes the page.
 *
 * The ground (Master Design System §12) is under every page of the shell —
 * the constructed still, or the live map once a page has claimed it with a
 * point — and the sheet is a leaf on it: the page goes transparent so the
 * ground shows through, the leaf drops 24px and fades as you leave, rises
 * 24px into place as you arrive, and on a subject page sits a band lower so
 * the ground can show the job. Only a GET is leaving: a form post is an
 * action on the page, and most come straight back to it, so the leaf stays
 * up while it runs. A visit that lands on the page it left keeps this same
 * layout instance, so the leaf is put back when the page arrives rather
 * than waiting for a remount that never comes.
 * Clicking the territory docks the leaf at the desk edge with its crumb
 * strip as the handle; clicking the strip, Escape, or the handle from the
 * keyboard brings it back, and the docked sheet's contents are inert so focus
 * never lands in what has left the screen. Below lg none of this applies:
 * the ground is not drawn, and the sheet is the plain page.
 */

type Leaf = "hidden" | "in" | "out"

const EASE_IN = "cubic-bezier(.4,0,1,1)"
const EASE_OUT = "cubic-bezier(0,0,.2,1)"
const EASE_DOCK = "cubic-bezier(.4,0,.2,1)"

export const ConsoleLayout = ({ children }: { children: ReactNode }) => {
  useGroundShell()
  const spec = useGroundSpec()
  const mode = useGroundStore((s) => s.mode)
  const dockOffset = useGroundStore((s) => s.dockOffset)
  const setMode = useGroundStore((s) => s.setMode)
  const lg = useMedia(LG)
  const mainRef = useRef<HTMLElement>(null)
  const handleRef = useRef<HTMLButtonElement>(null)
  const wasDocked = useRef(false)

  // a leaf arriving on a ground that is already there starts below and rises
  const [leaf, setLeaf] = useState<Leaf>(() =>
    groundOf(useGroundStore.getState()) ? "hidden" : "in",
  )

  const ground = spec !== null && lg
  const band = ground && spec.kind === "resolve"
  const docked = band && mode !== "sheet"

  // arrival: the show leaf rises from ~200ms in; the index leaf returns from ~120ms
  useEffect(() => {
    if (leaf !== "hidden") return
    const kind = useGroundStore.getState().spec?.kind
    const delay = reducedMotion()
      ? 0
      : kind === "resolve"
        ? MOTION.leafInDelay
        : MOTION.leafReturnDelay
    const t = setTimeout(() => setLeaf("in"), delay)
    return () => clearTimeout(t)
  }, [leaf])

  // departure: on a real navigation away from a grounded page the leaf drops
  // while the request runs; a visit that never lands puts it back, and one
  // that lands on this same page (a redirect back) puts it back on arrival
  useEffect(() => {
    const offStart = router.on("start", (event) => {
      const visit = event.detail.visit
      if (!groundOf(useGroundStore.getState())) return
      if (visit.method !== "get") return
      if (visit.only.length > 0) return
      if (visit.url.pathname === window.location.pathname) return
      setLeaf("out")
    })
    const back = () => setLeaf("in")
    const offFinish = router.on("finish", (event) => {
      const visit = event.detail.visit
      if (visit.cancelled || visit.interrupted) back()
    })
    const offNavigate = router.on("navigate", () => {
      setLeaf((current) => (current === "out" ? "in" : current))
    })
    const offError = router.on("error", back)
    const offInvalid = router.on("invalid", back)
    const offException = router.on("exception", back)
    return () => {
      offStart()
      offFinish()
      offNavigate()
      offError()
      offInvalid()
      offException()
    }
  }, [])

  // docking: the crumb strip is the handle, so the sheet always docks showing
  // it, and focus goes to the handle so the keyboard can bring the sheet back;
  // on restore focus returns to the sheet itself
  useEffect(() => {
    if (docked) {
      window.scrollTo({ top: 0 })
      handleRef.current?.focus({ preventScroll: true })
    } else if (wasDocked.current) {
      mainRef.current?.focus({ preventScroll: true })
    }
    wasDocked.current = docked
  }, [docked])

  useEffect(() => {
    if (!docked) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMode("sheet")
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [docked, setMode])

  // the sheet slides to the desk edge over 380ms; it comes back on the leaf's
  // quicker 240ms — retreat is quicker than arrival here too
  const transition = docked
    ? `transform ${MOTION.dock}ms ${EASE_DOCK}`
    : leaf === "out"
      ? `transform ${MOTION.leafOut}ms ${EASE_IN}, opacity ${MOTION.leafOut}ms ${EASE_IN}`
      : `transform ${MOTION.leafIn}ms ${EASE_OUT}, opacity ${MOTION.leafIn}ms ${EASE_OUT}`
  const travel = docked ? dockOffset : leaf === "in" ? 0 : 24
  const motion = ground
    ? {
        transform: `translateY(${travel}px)`,
        opacity: leaf === "in" ? 1 : 0,
        transition,
      }
    : undefined

  return (
    <div
      className={cx(
        "bg-secondary_subtle flex min-h-screen flex-col lg:flex-row",
        ground && "lg:bg-transparent",
      )}
    >
      <ConsoleRail />
      <main
        ref={mainRef}
        tabIndex={-1}
        onClick={docked ? () => setMode("sheet") : undefined}
        style={motion}
        className={cx(
          "lg:sheet relative z-10 min-w-0 flex-1 pt-6 pb-12 outline-none lg:mr-4 lg:pt-8",
          band
            ? "lg:mt-(--spacing-ground-band) lg:mb-3 lg:min-h-[calc(100vh-var(--spacing-ground-band)-12px)]"
            : "lg:my-3 lg:min-h-[calc(100vh-24px)]",
          docked && "cursor-pointer",
        )}
      >
        {docked && (
          // the handle: the crumb strip's height, over it, for the keyboard
          <button
            ref={handleRef}
            type="button"
            aria-label="Bring the job sheet back"
            onClick={() => setMode("sheet")}
            className="focus-visible:outline-focus-ring absolute inset-x-0 top-0 z-20 h-(--spacing-ground-handle) cursor-pointer rounded-t-xl outline-none focus-visible:outline-2 focus-visible:-outline-offset-2"
          />
        )}
        <div inert={docked || undefined}>{children}</div>
      </main>
    </div>
  )
}
